import { type ReactNode } from 'react';

import {
  type FormErrorMessageProps,
  type FormLabelProps,
} from '@chakra-ui/react';
import { type CountryCode } from 'libphonenumber-js';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import {
  PhoneNumberInput,
  type PhoneNumberInputProps,
} from '@/components/PhoneNumberInput';
import { getCountryCodeFromLanguage } from '@/utils/languageToCountry';

import { type FieldCommonProps } from '../FormField.types';

type CommonInputProps = Pick<
  PhoneNumberInputProps,
  | 'placeholder'
  | 'country'
  | 'withCountryCallingCode'
  | 'international'
  | 'useNationalFormatForDefaultCountryValue'
>;

export type FieldPhoneNumberProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'phone-number';
  label?: ReactNode;
  labelProps?: FormLabelProps;
  helper?: ReactNode;
  formFieldErrorProps?: FormErrorMessageProps;
  size?: PhoneNumberInputProps['size'];
} & FieldCommonProps<TFieldValues, TName> &
  CommonInputProps;

export const FieldPhoneNumber = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  labelProps,
  helper,
  formFieldErrorProps,

  // Input Props
  placeholder,
  country,
  withCountryCallingCode,
  international,
  useNationalFormatForDefaultCountryValue,
  size,

  ...controlProps
}: FieldPhoneNumberProps<TFieldValues, TName>) => {
  const { i18n } = useTranslation();
  const defaultCountry: CountryCode = getCountryCodeFromLanguage(i18n.language);

  const phoneInputProps = {
    size,
    placeholder,
    country: defaultCountry,
    ...(country && {
      withCountryCallingCode,
      international,
      useNationalFormatForDefaultCountryValue,
    }),
  };

  return (
    <Controller
      {...controlProps}
      render={({ field }) => (
        <FormFieldItem>
          {!!label && <FormFieldLabel {...labelProps}>{label}</FormFieldLabel>}
          <PhoneNumberInput {...field} {...phoneInputProps} />
          {!!helper && <FormFieldHelper>{helper}</FormFieldHelper>}
          <FormFieldError {...formFieldErrorProps} />
        </FormFieldItem>
      )}
    />
  );
};
